import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { trackMatomoEvent } from '../utils/matomoEvents';

// QR Campaign Configuration
interface QRCampaign {
  id: string;
  name: string;
  destination: string; // Internal path or full URL
  isExternal: boolean;
}

// Define your QR campaigns here with 10-digit alphanumeric IDs
const QR_CAMPAIGNS: Record<string, QRCampaign> = {
  'hl0ln02501': {
    id: 'hl0ln02501',
    name: 'QR Code - LinkedIn MINTkitchen',
    destination: 'https://www.linkedin.com/company/mintkitchen',
    isExternal: true,
  },
  'hl0yt02501': {
    id: 'hl0yt02501',
    name: 'QR Code - YouTube Video',
    destination: 'https://www.youtube.com/watch?v=9pL15l-IAqo',
    isExternal: true,
  },
  'hl0yt02502': {
    id: 'hl0yt02502',
    name: 'QR Code - YouTube Channel',
    destination: 'https://www.youtube.com/@mintkitchen',
    isExternal: true,
  },
};

const QRRedirect: React.FC = () => {
  const { campaignId } = useParams<{ campaignId: string }>();
  const navigate = useNavigate();
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    // Normalize campaign ID to lowercase for case-insensitive matching
    const normalizedId = campaignId?.toLowerCase();
    const campaign = normalizedId ? QR_CAMPAIGNS[normalizedId] : null;

    if (campaign) {
      // Track QR code scan in Matomo immediately
      trackMatomoEvent(
        'QR Code',
        'Scan',
        campaign.name,
        undefined
      );

      // Track with additional details
      if (typeof window !== 'undefined' && (window as any)._paq) {
        // Track campaign source
        (window as any)._paq.push(['setCustomDimension', 1, normalizedId]);
        
        // Track referrer as "QR Code"
        (window as any)._paq.push(['setReferrerUrl', `qr://${normalizedId}`]);
        
        console.log('📊 QR Code scan tracked:', {
          campaignId: normalizedId,
          campaignName: campaign.name,
          destination: campaign.destination,
        });
      }

      // Animate progress bar over 2 seconds
      const duration = 2000; // 2 seconds
      const interval = 20; // Update every 20ms
      const steps = duration / interval;
      let currentStep = 0;

      const progressTimer = setInterval(() => {
        currentStep++;
        setProgress((currentStep / steps) * 100);

        if (currentStep >= steps) {
          clearInterval(progressTimer);
        }
      }, interval);

      // Redirect after 2 seconds
      const redirectTimer = setTimeout(() => {
        if (campaign.isExternal) {
          // Redirect to external URL
          window.location.href = campaign.destination;
        } else {
          // Navigate internally
          navigate(campaign.destination);
        }
      }, duration);

      // Cleanup timers on unmount
      return () => {
        clearInterval(progressTimer);
        clearTimeout(redirectTimer);
      };

    } else {
      // Invalid campaign ID - redirect to home immediately
      console.warn('⚠️ Invalid QR campaign ID:', campaignId);
      trackMatomoEvent('QR Code', 'Invalid Scan', campaignId || 'undefined');
      
      setTimeout(() => {
        navigate('/');
      }, 1000);
    }
  }, [campaignId, navigate]);

  const normalizedId = campaignId?.toLowerCase();
  const campaign = normalizedId ? QR_CAMPAIGNS[normalizedId] : null;

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100vh',
      background: 'linear-gradient(135deg, #008F99 0%, #006b73 100%)',
      fontFamily: 'Arial, sans-serif',
      overflow: 'hidden',
    }}>
      {/* Logo Container with Animation */}
      <div style={{
        marginBottom: '40px',
        filter: 'brightness(0) invert(1) drop-shadow(2px 2px 8px rgba(0, 0, 0, 0.3))',
      }}>
        <img 
          src="/assets/images/common/mintKitchenLogo.svg"
          alt="MINTkitchen Logo"
          style={{
            height: '100px',
            width: 'auto',
            display: 'block',
            animation: 'fadeInScale 0.8s ease-out, pulse 2s ease-in-out',
          }}
          onError={(e) => {
            console.error('Logo failed to load');
            (e.target as HTMLImageElement).style.display = 'none';
          }}
        />
      </div>

      {/* Loading Text */}
      <div style={{
        color: 'white',
        fontSize: '20px',
        fontWeight: '500',
        marginBottom: '30px',
        animation: 'fadeIn 1s ease-out 0.3s both',
        textAlign: 'center',
        padding: '0 20px',
      }}>
        {campaign ? (
          <>
            <div style={{ marginBottom: '15px', fontSize: '18px' }}>
              Betriebsnahe Technologiebildung
            </div>
            <div style={{ 
              fontSize: '16px', 
              opacity: 0.9,
              fontWeight: 'normal',
            }}>
              Du wirst weitergeleitet...
            </div>
          </>
        ) : (
          <>
            <div style={{ marginBottom: '10px' }}>
              Ungültiger QR-Code
            </div>
            <div style={{ 
              fontSize: '16px', 
              opacity: 0.9,
              fontWeight: 'normal',
            }}>
              Weiterleitung zur Startseite...
            </div>
          </>
        )}
      </div>

      {/* Progress Bar Container */}
      <div style={{
        width: '300px',
        maxWidth: '80%',
        height: '4px',
        backgroundColor: 'rgba(255, 255, 255, 0.3)',
        borderRadius: '2px',
        overflow: 'hidden',
        animation: 'fadeIn 1s ease-out 0.5s both',
      }}>
        {/* Progress Bar Fill */}
        <div style={{
          height: '100%',
          width: `${progress}%`,
          backgroundColor: 'white',
          transition: 'width 0.02s linear',
          borderRadius: '2px',
          boxShadow: '0 0 10px rgba(255, 255, 255, 0.5)',
        }} />
      </div>

      {/* CSS Animations */}
      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes fadeInScale {
          from {
            opacity: 0;
            transform: scale(0.8);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }

        @keyframes pulse {
          0%, 100% {
            transform: scale(1);
          }
          50% {
            transform: scale(1.05);
          }
        }

        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        /* Responsive adjustments */
        @media (max-width: 768px) {
          img {
            height: 80px !important;
          }
        }
      `}</style>
    </div>
  );
};

export default QRRedirect;

